import {Box, HStack, Icon, Show, Text, ThemeIconKey, VStack} from 'platform/foundation';
import {cva} from 'platform/shared';

import {ChangeEvent, useId} from 'react';

import {defaultTo} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {getDataAttribute} from '../../utils/getDataAttribute';
import {HelperText} from '../HelperText/HelperText';
import {CheckboxPropsBase} from './types';

export interface CheckboxProps extends CheckboxPropsBase, FormControlProps<boolean>, TestIdProps {
  isIndeterminate?: boolean;
  indeterminateIcon?: ThemeIconKey;
  // A unique ID for the input element, allowing it to be associated with a <label> element in another component.
  externalLabelId?: string;
  'aria-label'?: string;
}

const CheckboxWrapper = 'label';

const checkboxWrapperVariants = cva('relative cursor-pointer', {
  variants: {
    isDisabled: {
      true: 'cursor-not-allowed',
      false: '',
    },
  },
});

const checkboxInputWrapperVariants = cva(
  'relative inline-flex [&>input:active:not(:disabled)+span]:[box-shadow:0_0_0_4px_var(--color-palettes-blue-30-100)] [&>input:focus+span]:[box-shadow:0_0_0_4px_var(--color-palettes-blue-30-100)] [&>input:hover:not(:disabled)+span]:[box-shadow:0_0_0_4px_var(--color-palettes-blue-20-100)]',
  {
    variants: {
      isDisabled: {
        true: 'opacity-40',
        false: '',
      },
    },
  }
);

const checkboxInputVariants = cva(
  'absolute z-[1] h-[calc(var(--eag-space-step,0.25)*5rem)] w-[calc(var(--eag-space-step,0.25)*5rem)] cursor-pointer opacity-0 disabled:cursor-not-allowed'
);

const checkboxControlVariants = cva(
  'pointer-events-none inline-flex h-[calc(var(--eag-space-step,0.25)*5rem)] w-[calc(var(--eag-space-step,0.25)*5rem)] shrink-0 select-none items-center justify-center rounded-[var(--radius-small)] border border-[var(--color-palettes-neutral-200-100)] bg-[var(--color-palettes-white-10-100)] text-[var(--color-palettes-white-10-100)]',
  {
    variants: {
      isInvalid: {
        true: 'border-[var(--color-severity-danger)]',
        false: '',
      },
      isChecked: {
        true: 'border-[var(--color-accent)] bg-[var(--color-accent)]',
        false: '',
      },
      isIndeterminate: {
        true: 'border-[var(--color-transparent)] bg-[var(--color-palettes-neutral-70-100)]',
        false: '',
      },
    },
  }
);

export function Checkbox(props: CheckboxProps) {
  const id = useId();
  const indeterminateIcon = defaultTo('toggle/minus', props.indeterminateIcon);
  const checkedIcon = defaultTo('toggle/tick', props.checkedIcon);

  const isInvalid = props.isInvalid || !!props.errorMessage;
  const icon = props.isIndeterminate ? indeterminateIcon : checkedIcon;
  const shouldIconShow = props.value || props.isIndeterminate;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (props.isDisabled) {
      event.preventDefault();
      return;
    }

    props.onChange?.(!props.value, event);
  };

  const rootProps = {
    'data-disabled': getDataAttribute(props.isDisabled),
    'data-checked': getDataAttribute(props.value),
    'data-invalid': getDataAttribute(isInvalid),
  };

  const checkboxProps = {
    'data-checked': getDataAttribute(props.value && !props.isIndeterminate),
    'data-disabled': getDataAttribute(props.isDisabled),
    'data-invalid': getDataAttribute(isInvalid),
    'data-indeterminate': getDataAttribute(props.isIndeterminate && !props.value),
  };

  return (
    <VStack>
      <CheckboxWrapper
        {...rootProps}
        data-testid={suffixTestId('checkbox', props)}
        className={checkboxWrapperVariants({isDisabled: Boolean(props.isDisabled)})}
      >
        <HStack>
          <div
            {...rootProps}
            className={checkboxInputWrapperVariants({
              isDisabled: Boolean(props.isDisabled),
            })}
          >
            <input
              id={props.externalLabelId ?? id}
              type="checkbox"
              checked={props.value}
              onChange={handleChange}
              onFocus={props.onFocus}
              onBlur={props.onBlur}
              disabled={props.isDisabled}
              required={props.isRequired}
              aria-checked={props.isIndeterminate ? 'mixed' : props.value}
              aria-label={props['aria-label']}
              aria-labelledby={
                props['aria-label'] ? undefined : `${props.externalLabelId ?? id}-label`
              }
              aria-invalid={isInvalid || undefined}
              data-testid={suffixTestId('checkboxInput', props)}
              className={checkboxInputVariants()}
            />
            <span
              {...checkboxProps}
              data-testid={suffixTestId('checkboxControl', props)}
              className={checkboxControlVariants({
                isInvalid,
                isChecked: Boolean(props.value && !props.isIndeterminate),
                isIndeterminate: Boolean(props.isIndeterminate && !props.value),
              })}
            >
              <Show when={shouldIconShow}>
                <Box flex={0} padding={0}>
                  <Icon value={icon} size={3} />
                </Box>
              </Show>
            </span>
          </div>
          <Show when={props.label}>
            <Box paddingLeft={3}>
              <Show when={props.label}>
                <Text
                  size="small"
                  id={`${id}-label`}
                  data-testid={suffixTestId('checkboxLabel', props)}
                >
                  {props.label}
                </Text>
              </Show>
              <Show when={props.helperText}>
                <HelperText
                  helperText={props.helperText}
                  data-testid={suffixTestId('helper', props)}
                />
              </Show>
              <Show when={props.errorMessage}>
                <HelperText
                  errorMessage={props.errorMessage}
                  data-testid={suffixTestId('errorMessage', props)}
                />
              </Show>
            </Box>
          </Show>
        </HStack>
      </CheckboxWrapper>
    </VStack>
  );
}
